import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/editor-fields';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';
import { cn } from '@/lib/utils';
import { useSetSharedStore, useSharedStores } from './inventory.api';

/**
 * Stores whose stock moves together with this one.
 *
 * Linking is one store per request — `UpdateSharedInventoryStoreBindingModel`
 * is a single pair of ids — so the dialog adds and removes one row at a time
 * rather than offering a multi-select that would quietly fan out into a burst
 * of requests behind a single Save, with no sensible behaviour if one failed.
 */
export function SharedStoresDialog({
  businessId,
  businessName,
  open,
  onOpenChange,
}: {
  businessId: string;
  businessName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [toAdd, setToAdd] = useState('');

  const shared = useSharedStores(open ? businessId : null);
  const { data: businesses } = useBusinesses();
  const link = useSetSharedStore(businessId);

  const linked = shared.data ?? [];
  const available = (businesses ?? []).filter(
    (business) =>
      business.id !== businessId &&
      !linked.some((store) => store.id === business.id)
  );

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('inventory.actionFailed')
    );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('inventory.sharedStores')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('inventory.sharedStoresBody', { store: businessName })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <Field label={t('inventory.linkStore')}>
            <div className="flex gap-2">
              <Select
                value={toAdd}
                disabled={available.length === 0 || link.isPending}
                onValueChange={setToAdd}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('inventory.chooseStoreToLink')} />
                </SelectTrigger>
                <SelectContent>
                  {available.map((business) => (
                    <SelectItem key={business.id} value={business.id}>
                      {business.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                disabled={!toAdd || link.isPending}
                onClick={() =>
                  link.mutate(
                    { sharedBusinessId: toAdd, remove: false },
                    {
                      onSuccess: () => setToAdd(''),
                      onError: failed,
                    }
                  )
                }
              >
                {link.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Plus className="size-4" />
                )}
                {t('inventory.link')}
              </Button>
            </div>
          </Field>

          {shared.isPending && <Skeleton className="h-20 w-full rounded-xl" />}

          {shared.isSuccess && linked.length === 0 && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
              {t('inventory.noSharedStores')}
            </p>
          )}

          {linked.length > 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={shared.isFetching} />
              <div
                className={cn(
                  'max-h-56 divide-y overflow-y-auto rounded-xl border',
                  shared.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {linked.map((store) => (
                  <div
                    key={store.id}
                    className="flex items-center gap-2 p-3 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">{store.name}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('inventory.unlink')}
                      disabled={link.isPending}
                      onClick={() =>
                        link.mutate(
                          { sharedBusinessId: store.id, remove: true },
                          { onError: failed }
                        )
                      }
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogAction>{t('inventory.done')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
